import { Button, Divider, Popover, Tooltip } from 'antd'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import ColumnFilterDateRange from './ColumnFilterDateRange';
import _ from 'lodash';
import { CalendarIcon, SwapRightIcon } from './Icons'
import { CellRenderers } from 'utils/tableUtils';
import { DateRangeQuickOptions, getStartAndEndDateForQuickOption } from 'utils/dateUtils';
import { useGlobalFilterContext } from 'contexts/GlobalFilterContext'
import { withRouter } from 'react-router';
import { parseURLSearch, updateURLSearch } from 'utils/urlUtil';

function DurationPicker({ filterKey = "foundOn", className, onChange, selectedDurationValue, value, btnProps, location, history, preventURLClear = false, allowFutureDates }) {// selectedDurationValue is used to update date by passing props
    const selected = useRef();
    const globalFilter = useGlobalFilterContext();
    const [globalFilterHash] = useState(globalFilter.globalFilterHash)
    const [quickSelected, setQuickSelected] = useState(() => {
        if(!_.isFunction(onChange)){
            const { filters } = parseURLSearch(location.search);
            const selectedKey = _.get(filters, "selectedDuration");
            return _.find(DateRangeQuickOptions, { label: selectedKey })?.label;
        }
        if(value && value.length === 1){
            return _.find(DateRangeQuickOptions, { label: value[0] })?.label;
        }
    });
    const [quickSelectedPrevious, setQuickSelectedPrevious] = useState(quickSelected);
    const [selectedPrevious, setSelectedPrevious] = useState(() => {
        if(quickSelectedPrevious){
            return getStartAndEndDateForQuickOption({ label: quickSelectedPrevious });
        }
        if(value && value.length === 2){
            return value;
        }
        const { filters } = parseURLSearch(location?.search);
        return _.get(filters, [filterKey], []);
    });
    const [visible, setVisible] = useState(false);

    const setSelected = useCallback((values) => {
        selected.current = [...values]
    }, [selected]);

    const applyDuration = useCallback((values, selectedDuration) => {
        const { filters, pagination } = parseURLSearch(location?.search);
        pagination.page = 0;
        delete filters[filterKey];
        delete filters.selectedDuration;
        if(selectedDuration && selectedDuration !== "All data"){
            filters.selectedDuration = selectedDuration;
        } else if(!_.isEmpty(values)){
            filters[filterKey] = [...values];
        }
        onChange ? onChange(filters) : updateURLSearch(history, { filters , pagination });
    }, [location?.search, history, filterKey, onChange]);

    useEffect(() => {
        if(_.isArray(value)){
            if(value.length === 1){
                setQuickSelectedPrevious(value[0]);
            } else {
                setSelectedPrevious(value);
            }
        }
    }, [value]);

    useEffect(() => {
        if (_.isEmpty(selectedDurationValue) && !_.isFunction(onChange)) {
            const { filters } = parseURLSearch(location.search);
            setSelectedPrevious(filters[filterKey] || []);
            setQuickSelectedPrevious(filters.selectedDuration);
        } else if(selectedDurationValue) {
            const label =  _.find(DateRangeQuickOptions, { label: selectedDurationValue })?.label;
            setQuickSelectedPrevious(label)
        }
    }, [location?.search, filterKey, selectedDurationValue, onChange]);

    useEffect(() => {
        if(!preventURLClear && globalFilterHash !== globalFilter.globalFilterHash){
            onChange ? onChange({}) : updateURLSearch(history, { filters: {} });
        }
    }, [globalFilter.globalFilterHash, globalFilterHash, history, onChange, preventURLClear])

    const confirm = useCallback(() => {
        setSelectedPrevious([...selected.current])
        setQuickSelectedPrevious(quickSelected)
        setVisible(false);
        applyDuration(selected.current, quickSelected);
    }, [selected, quickSelected, applyDuration]);

    useEffect(() => {
        if(!visible){
            setSelected([...selectedPrevious]);
            setQuickSelected(quickSelectedPrevious);
        }
    }, [visible, selectedPrevious, quickSelectedPrevious, setSelected]);

    const popoverContent = (
        <div style={{margin: "-12px -16px"}}>
            <ColumnFilterDateRange 
                key={visible}
                selectedKeys={selectedPrevious}
                setSelectedKeys={setSelected}
                quickSelected={quickSelected}
                visible={visible}
                confirm={confirm}
                setQuickSelected={setQuickSelected}
                quickOptions
                allowFutureDates={allowFutureDates}
            />
        </div>
    )
    return (
        <Popover trigger={["click"]} visible={visible} content={popoverContent} onVisibleChange={!btnProps?.disabled ? setVisible : null} placement="bottomRight">
            <Button className={className} size="small" style={{ minWidth: 200 }} {...btnProps}>
                <div className="d-flex align-items-center justify-content-between">
                    <span>
                        {
                            quickSelectedPrevious || _.isEmpty(selectedPrevious)
                            ?
                            (quickSelectedPrevious || "All data")
                            :
                            <>
                                <Tooltip title={CellRenderers.TIME(selectedPrevious[0])}>{CellRenderers.DATE(selectedPrevious[0])}</Tooltip>
                                <SwapRightIcon className="m-x-xs" />
                                <Tooltip title={CellRenderers.TIME(selectedPrevious[1])}>{CellRenderers.DATE(selectedPrevious[1])}</Tooltip>
                            </>    
                        }
                    </span>
                    <span>
                        <Divider type="vertical" />
                        <CalendarIcon />
                    </span>
                </div>
            </Button>
        </Popover>
    )
}

export default withRouter(DurationPicker);

export {
    DurationPicker as DurationPickerBase
}